import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  api,
  type EarningsSummary,
  type StripeConnectStatus,
  type Payout,
  type Gallery,
} from '@/lib/api';

// Loading skeleton component
function Skeleton({ className = '' }: { className?: string }) {
  return (
    <div
      className={`animate-pulse bg-gallery-700 rounded ${className}`}
      aria-hidden="true"
    />
  );
}

// Format currency
function formatCurrency(cents: number, currency = 'usd'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

// Format date
function formatDate(date: string | number): string {
  const d = typeof date === 'number' ? new Date(date * 1000) : new Date(date);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function DashboardNav() {
  const location = useLocation();

  const navItems = [
    { path: '/artist', label: 'Overview', exact: true },
    { path: '/artist/galleries', label: 'Galleries' },
    { path: '/artist/upload', label: 'Upload' },
    { path: '/artist/earnings', label: 'Earnings' },
    { path: '/artist/settings', label: 'Settings' },
  ];

  return (
    <nav className="flex gap-1 p-1 bg-gallery-900 rounded-lg mb-8 overflow-x-auto">
      {navItems.map((item) => {
        const isActive = item.exact
          ? location.pathname === item.path
          : location.pathname.startsWith(item.path);

        return (
          <Link
            key={item.path}
            to={item.path}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              isActive
                ? 'bg-gallery-800 text-white'
                : 'text-gallery-400 hover:text-gallery-200'
            }`}
          >
            {item.label}
          </Link>
        );
      })}
    </nav>
  );
}

function Overview() {
  const [earnings, setEarnings] = useState<EarningsSummary | null>(null);
  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [stripeStatus, setStripeStatus] = useState<StripeConnectStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadData() {
      try {
        setIsLoading(true);
        const [earningsData, galleriesData, stripeData] = await Promise.all([
          api.getArtistEarnings().catch(() => null),
          api.getMyGalleries().catch(() => ({ galleries: [] })),
          api.getStripeConnectStatus().catch(() => null),
        ]);

        setEarnings(earningsData);
        setGalleries(galleriesData?.galleries || []);
        setStripeStatus(stripeData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setIsLoading(false);
      }
    }

    loadData();
  }, []);

  if (isLoading) {
    return (
      <div>
        <h2 className="text-2xl font-semibold mb-6">Dashboard Overview</h2>
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {[1, 2, 3].map((i) => (
            <div key={i} className="p-6 bg-gallery-900 rounded-xl">
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-8 w-32" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-900/20 border border-red-800 rounded-xl">
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  const totalViews = galleries.reduce((sum, g) => sum + (g.viewCount || 0), 0);

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6">Dashboard Overview</h2>

      {/* Stripe Connect Warning */}
      {stripeStatus && !stripeStatus.onboardingComplete && (
        <div className="p-4 mb-6 bg-amber-900/20 border border-amber-700 rounded-xl flex items-center justify-between">
          <div>
            <p className="font-medium text-amber-200">Complete your Stripe setup</p>
            <p className="text-sm text-amber-400">
              Finish connecting your Stripe account to receive payments.
            </p>
          </div>
          <Link
            to="/artist/earnings"
            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 rounded-lg text-sm font-medium transition-colors"
          >
            Complete Setup
          </Link>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <div className="p-6 bg-gallery-900 rounded-xl">
          <p className="text-gallery-500 text-sm mb-1">Total Earnings</p>
          <p className="text-3xl font-bold">
            {formatCurrency(earnings?.netEarningsCents || 0)}
          </p>
          {earnings && earnings.totalFeesCents > 0 && (
            <p className="text-sm text-gallery-500 mt-1">
              {formatCurrency(earnings.totalFeesCents)} in fees
            </p>
          )}
        </div>
        <div className="p-6 bg-gallery-900 rounded-xl">
          <p className="text-gallery-500 text-sm mb-1">Galleries</p>
          <p className="text-3xl font-bold">{galleries.length}</p>
          <p className="text-sm text-gallery-500 mt-1">
            {galleries.filter((g) => g.isPublic).length} public
          </p>
        </div>
        <div className="p-6 bg-gallery-900 rounded-xl">
          <p className="text-gallery-500 text-sm mb-1">Total Views</p>
          <p className="text-3xl font-bold">{totalViews.toLocaleString()}</p>
        </div>
      </div>

      {/* Stripe Balance */}
      {earnings?.stripeBalance && (
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="p-6 bg-gallery-900 rounded-xl">
            <p className="text-gallery-500 text-sm mb-1">Available Balance</p>
            <p className="text-2xl font-bold text-green-400">
              {earnings.stripeBalance.available.map((b) =>
                formatCurrency(b.amount, b.currency)
              ).join(', ') || '$0.00'}
            </p>
            <p className="text-sm text-gallery-500 mt-1">Ready for payout</p>
          </div>
          <div className="p-6 bg-gallery-900 rounded-xl">
            <p className="text-gallery-500 text-sm mb-1">Pending Balance</p>
            <p className="text-2xl font-bold text-yellow-400">
              {earnings.stripeBalance.pending.map((b) =>
                formatCurrency(b.amount, b.currency)
              ).join(', ') || '$0.00'}
            </p>
            <p className="text-sm text-gallery-500 mt-1">Processing</p>
          </div>
        </div>
      )}

      {/* Recent Sales */}
      {earnings && earnings.recentOrders.length > 0 && (
        <div className="bg-gallery-900 rounded-xl p-6">
          <h3 className="font-semibold mb-4">Recent Sales</h3>
          <div className="space-y-3">
            {earnings.recentOrders.slice(0, 5).map((order) => (
              <div
                key={order.id}
                className="flex items-center justify-between py-2 border-b border-gallery-800 last:border-0"
              >
                <div>
                  <p className="font-medium">{order.galleryTitle}</p>
                  <p className="text-sm text-gallery-500">{formatDate(order.createdAt)}</p>
                </div>
                <p className="font-medium text-green-400">
                  +{formatCurrency(order.artistShareCents)}
                </p>
              </div>
            ))}
          </div>
          <Link
            to="/artist/earnings"
            className="block text-center mt-4 text-sm text-accent-400 hover:text-accent-300"
          >
            View all transactions
          </Link>
        </div>
      )}

      {/* Empty state */}
      {galleries.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gallery-400 mb-4">No galleries yet. Create your first gallery to get started.</p>
          <Link
            to="/artist/galleries"
            className="inline-block px-6 py-3 bg-accent-600 hover:bg-accent-700 rounded-lg font-medium transition-colors"
          >
            Create Gallery
          </Link>
        </div>
      )}
    </div>
  );
}

function Galleries() {
  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.getMyGalleries()
      .then((data) => setGalleries(data.galleries))
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading) {
    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold">Your Galleries</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-gallery-900 rounded-xl overflow-hidden">
              <Skeleton className="h-40 w-full rounded-none" />
              <div className="p-4">
                <Skeleton className="h-5 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold">Your Galleries</h2>
        <button
          onClick={() => navigate('/artist/galleries/new')}
          className="px-4 py-2 bg-accent-600 hover:bg-accent-700 rounded-lg text-sm font-medium transition-colors"
        >
          Create Gallery
        </button>
      </div>

      {galleries.length === 0 ? (
        <div className="text-center py-12 bg-gallery-900 rounded-xl">
          <p className="text-gallery-400 mb-4">No galleries yet. Create your first gallery to get started.</p>
          <button
            onClick={() => navigate('/artist/galleries/new')}
            className="px-6 py-3 bg-accent-600 hover:bg-accent-700 rounded-lg font-medium transition-colors"
          >
            Create Gallery
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {galleries.map((gallery) => (
            <Link
              key={gallery.id}
              to={`/galleries/${gallery.id}`}
              className="bg-gallery-900 rounded-xl overflow-hidden hover:ring-2 hover:ring-accent-500 transition-all"
            >
              <div className="h-40 bg-gallery-800 flex items-center justify-center">
                {gallery.coverImageUrl ? (
                  <img
                    src={gallery.coverImageUrl}
                    alt={gallery.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-gallery-600 text-4xl">🖼️</span>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-medium truncate">{gallery.title}</h3>
                  {!gallery.isPublic && (
                    <span className="px-2 py-0.5 text-xs bg-gallery-700 rounded">Draft</span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-sm text-gallery-500">
                  <span>{gallery.viewCount} views</span>
                  <span>
                    {gallery.entryPriceCents === 0
                      ? 'Free'
                      : formatCurrency(gallery.entryPriceCents)}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function Upload() {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    console.log('Dropped files:', files);
    // TODO: Implement upload
  };

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6">Upload Artwork</h2>
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
          isDragging
            ? 'border-accent-500 bg-accent-500/10'
            : 'border-gallery-700 hover:border-gallery-600'
        }`}
      >
        <p className="text-gallery-400 mb-4">Drag and drop files here, or click to select</p>
        <label className="inline-block px-6 py-3 bg-gallery-800 hover:bg-gallery-700 rounded-lg cursor-pointer transition-colors">
          Select Files
          <input
            type="file"
            className="hidden"
            multiple
            accept="image/*,video/*"
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              console.log('Selected files:', files);
              // TODO: Implement upload
            }}
          />
        </label>
        <p className="text-gallery-500 text-sm mt-4">
          Supported: JPEG, PNG, WebP, GIF, MP4, MOV, WebM
        </p>
      </div>
    </div>
  );
}

function Earnings() {
  const [earnings, setEarnings] = useState<EarningsSummary | null>(null);
  const [stripeStatus, setStripeStatus] = useState<StripeConnectStatus | null>(null);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');

  useEffect(() => {
    async function loadData() {
      try {
        setIsLoading(true);
        const [earningsData, stripeData, payoutsData] = await Promise.all([
          api.getArtistEarnings().catch(() => null),
          api.getStripeConnectStatus().catch(() => null),
          api.getArtistPayouts().catch(() => ({ payouts: [] })),
        ]);

        setEarnings(earningsData);
        setStripeStatus(stripeData);
        setPayouts(payoutsData?.payouts || []);
      } catch (err) {
        console.error('Failed to load earnings:', err);
      } finally {
        setIsLoading(false);
      }
    }

    loadData();
  }, []);

  const handleConnectStripe = async () => {
    try {
      setIsConnecting(true);
      const { url } = await api.startStripeConnect();
      window.location.href = url;
    } catch (err) {
      console.error('Failed to start Stripe Connect:', err);
      alert('Failed to start Stripe setup. Please try again.');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleRefreshOnboarding = async () => {
    try {
      setIsConnecting(true);
      const { url } = await api.refreshStripeOnboarding();
      window.location.href = url;
    } catch (err) {
      console.error('Failed to refresh onboarding:', err);
      alert('Failed to continue Stripe setup. Please try again.');
    } finally {
      setIsConnecting(false);
    }
  };

  if (isLoading) {
    return (
      <div>
        <h2 className="text-2xl font-semibold mb-6">Earnings</h2>
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {[1, 2, 3].map((i) => (
            <div key={i} className="p-6 bg-gallery-900 rounded-xl">
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-8 w-32" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Not connected to Stripe
  if (!stripeStatus?.connected) {
    return (
      <div>
        <h2 className="text-2xl font-semibold mb-6">Earnings</h2>
        <div className="text-center py-12 bg-gallery-900 rounded-xl">
          <div className="w-16 h-16 mx-auto mb-4 bg-purple-500/20 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold mb-2">Connect with Stripe</h3>
          <p className="text-gallery-400 mb-6 max-w-md mx-auto">
            Connect your Stripe account to receive payments from your gallery sales.
            Stripe handles all payment processing securely.
          </p>
          <button
            onClick={handleConnectStripe}
            disabled={isConnecting}
            className="px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isConnecting ? 'Connecting...' : 'Connect with Stripe'}
          </button>
        </div>
      </div>
    );
  }

  // Onboarding incomplete
  if (!stripeStatus.onboardingComplete) {
    return (
      <div>
        <h2 className="text-2xl font-semibold mb-6">Earnings</h2>
        <div className="text-center py-12 bg-gallery-900 rounded-xl">
          <div className="w-16 h-16 mx-auto mb-4 bg-amber-500/20 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold mb-2">Complete Your Setup</h3>
          <p className="text-gallery-400 mb-4 max-w-md mx-auto">
            Your Stripe account needs additional information before you can receive payments.
          </p>
          {stripeStatus.requiresInfo && stripeStatus.requiresInfo.length > 0 && (
            <ul className="text-sm text-gallery-500 mb-6 space-y-1">
              {stripeStatus.requiresInfo.map((item) => (
                <li key={item}>{item.replace(/_/g, ' ')}</li>
              ))}
            </ul>
          )}
          <button
            onClick={handleRefreshOnboarding}
            disabled={isConnecting}
            className="px-6 py-3 bg-amber-600 hover:bg-amber-700 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {isConnecting ? 'Loading...' : 'Continue Setup'}
          </button>
        </div>
      </div>
    );
  }

  // Filter orders by date range
  const filterByDateRange = (orders: EarningsSummary['recentOrders']) => {
    if (dateRange === 'all') return orders;

    const now = Date.now();
    const ranges: Record<string, number> = {
      '7d': 7 * 24 * 60 * 60 * 1000,
      '30d': 30 * 24 * 60 * 60 * 1000,
      '90d': 90 * 24 * 60 * 60 * 1000,
    };

    const cutoff = now - ranges[dateRange];
    return orders.filter((o) => new Date(o.createdAt).getTime() > cutoff);
  };

  const filteredOrders = earnings ? filterByDateRange(earnings.recentOrders) : [];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold">Earnings</h2>
        <div className="flex gap-2">
          {(['7d', '30d', '90d', 'all'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setDateRange(range)}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                dateRange === range
                  ? 'bg-accent-600 text-white'
                  : 'bg-gallery-800 text-gallery-400 hover:text-white'
              }`}
            >
              {range === 'all' ? 'All Time' : range.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Earnings Summary */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <div className="p-6 bg-gallery-900 rounded-xl">
          <p className="text-gallery-500 text-sm mb-1">Total Earned</p>
          <p className="text-3xl font-bold">
            {formatCurrency(earnings?.totalEarnedCents || 0)}
          </p>
        </div>
        <div className="p-6 bg-gallery-900 rounded-xl">
          <p className="text-gallery-500 text-sm mb-1">Platform Fees</p>
          <p className="text-3xl font-bold text-gallery-400">
            {formatCurrency(earnings?.totalFeesCents || 0)}
          </p>
        </div>
        <div className="p-6 bg-gallery-900 rounded-xl">
          <p className="text-gallery-500 text-sm mb-1">Net Earnings</p>
          <p className="text-3xl font-bold text-green-400">
            {formatCurrency(earnings?.netEarningsCents || 0)}
          </p>
        </div>
      </div>

      {/* Stripe Balance */}
      {earnings?.stripeBalance && (
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="p-6 bg-gallery-900 rounded-xl border border-green-800">
            <p className="text-gallery-500 text-sm mb-1">Available for Payout</p>
            <p className="text-2xl font-bold text-green-400">
              {earnings.stripeBalance.available.map((b) =>
                formatCurrency(b.amount, b.currency)
              ).join(', ') || '$0.00'}
            </p>
          </div>
          <div className="p-6 bg-gallery-900 rounded-xl border border-yellow-800">
            <p className="text-gallery-500 text-sm mb-1">Pending</p>
            <p className="text-2xl font-bold text-yellow-400">
              {earnings.stripeBalance.pending.map((b) =>
                formatCurrency(b.amount, b.currency)
              ).join(', ') || '$0.00'}
            </p>
          </div>
        </div>
      )}

      {/* Recent Orders */}
      <div className="bg-gallery-900 rounded-xl p-6 mb-8">
        <h3 className="font-semibold mb-4">
          Recent Sales ({filteredOrders.length})
        </h3>
        {filteredOrders.length === 0 ? (
          <p className="text-gallery-500 text-center py-8">
            No sales in this period.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-gallery-500 border-b border-gallery-800">
                  <th className="pb-3 font-medium">Gallery</th>
                  <th className="pb-3 font-medium">Customer</th>
                  <th className="pb-3 font-medium">Date</th>
                  <th className="pb-3 font-medium text-right">Amount</th>
                  <th className="pb-3 font-medium text-right">Your Share</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order) => (
                  <tr key={order.id} className="border-b border-gallery-800 last:border-0">
                    <td className="py-3">{order.galleryTitle}</td>
                    <td className="py-3 text-gallery-400">{order.buyerEmail}</td>
                    <td className="py-3 text-gallery-400">{formatDate(order.createdAt)}</td>
                    <td className="py-3 text-right">{formatCurrency(order.amount)}</td>
                    <td className="py-3 text-right text-green-400">
                      {formatCurrency(order.artistShareCents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payouts */}
      {payouts.length > 0 && (
        <div className="bg-gallery-900 rounded-xl p-6">
          <h3 className="font-semibold mb-4">Payout History</h3>
          <div className="space-y-3">
            {payouts.map((payout) => (
              <div
                key={payout.id}
                className="flex items-center justify-between py-3 border-b border-gallery-800 last:border-0"
              >
                <div>
                  <p className="font-medium">
                    {formatCurrency(payout.amount, payout.currency)}
                  </p>
                  <p className="text-sm text-gallery-500">
                    {payout.status === 'paid'
                      ? `Paid on ${formatDate(payout.arrivalDate)}`
                      : `Expected ${formatDate(payout.arrivalDate)}`}
                  </p>
                </div>
                <span
                  className={`px-2 py-1 text-xs rounded ${
                    payout.status === 'paid'
                      ? 'bg-green-900/50 text-green-400'
                      : payout.status === 'pending'
                      ? 'bg-yellow-900/50 text-yellow-400'
                      : 'bg-gallery-800 text-gallery-400'
                  }`}
                >
                  {payout.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Settings() {
  const { user } = useAuth();

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6">Settings</h2>
      <div className="space-y-6">
        <div className="p-6 bg-gallery-900 rounded-xl">
          <h3 className="font-semibold mb-4">Profile</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gallery-400 mb-1">Name</label>
              <input
                type="text"
                defaultValue={user?.name}
                className="w-full px-4 py-2 bg-gallery-800 border border-gallery-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-500"
              />
            </div>
            <div>
              <label className="block text-sm text-gallery-400 mb-1">Email</label>
              <input
                type="email"
                defaultValue={user?.email}
                disabled
                className="w-full px-4 py-2 bg-gallery-800 border border-gallery-700 rounded-lg opacity-60"
              />
            </div>
            <button className="px-4 py-2 bg-accent-600 hover:bg-accent-700 rounded-lg text-sm font-medium transition-colors">
              Save Changes
            </button>
          </div>
        </div>
        <div className="p-6 bg-gallery-900 rounded-xl">
          <h3 className="font-semibold mb-4">Connected Wallets</h3>
          <p className="text-gallery-400 text-sm mb-4">
            Connect wallets for NFT minting and Web3 features.
          </p>
          <button className="px-4 py-2 bg-gallery-800 hover:bg-gallery-700 rounded-lg text-sm font-medium transition-colors">
            Connect Wallet
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ArtistDashboard() {
  const { isAuthenticated, isLoading, isArtist } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login?redirect=/artist');
    }
  }, [isLoading, isAuthenticated, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-accent-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (!isArtist) {
    return (
      <div className="min-h-screen p-8">
        <div className="max-w-lg mx-auto text-center">
          <h1 className="text-2xl font-bold mb-4">Artist Access Required</h1>
          <p className="text-gallery-400 mb-6">
            You need an artist account to access this dashboard.
          </p>
          <button className="px-6 py-3 bg-accent-600 hover:bg-accent-700 rounded-lg font-medium transition-colors">
            Apply to Become an Artist
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8 max-w-7xl mx-auto">
      <h1 className="font-display text-3xl md:text-4xl font-bold mb-8">Artist Dashboard</h1>
      <DashboardNav />

      <Routes>
        <Route index element={<Overview />} />
        <Route path="galleries" element={<Galleries />} />
        <Route path="upload" element={<Upload />} />
        <Route path="earnings" element={<Earnings />} />
        <Route path="settings" element={<Settings />} />
      </Routes>
    </div>
  );
}
